<ng-container *transloco="let t; read: 'time-entries'">
  <form [formGroup]="filterForm" class="grid col2 mbl">
    <div class="span3 flex flexDirRow alignCenter gaps flexWrap">
      <div class="flexItem">
        <label for="fiscalYear">{{ t('evaluation.fiscalYear') }}</label>
        <kendo-dropdownlist
          formControlName="fiscalYear"
          id="fiscalYear"
          [data]="fiscalYears$ | async"
          textField="id"
          valueField="id"
          [valuePrimitive]="false"
          [defaultItem]="fiscalYearDefaultItem"
        >
          <ng-template kendoDropDownListItemTemplate let-dataItem>
            {{ dataItem?.id ? fiscalYearLabel(dataItem) : t('evaluation.chooseFiscalYear') }}
          </ng-template>
          <ng-template kendoDropDownListValueTemplate let-dataItem>
            {{ dataItem?.id ? fiscalYearLabel(dataItem) : t('evaluation.chooseFiscalYear') }}
          </ng-template>
        </kendo-dropdownlist>
      </div>

      <div class="flexItem">
        <label for="months">{{ t('evaluation.months') }}</label>
        <kendo-multiselect
          formControlName="months"
          id="months"
          [data]="monthOptions"
          textField="label"
          valueField="label"
          [valuePrimitive]="false"
          [placeholder]="t('all')"
          [autoClose]="false"
        ></kendo-multiselect>
      </div>

      <div class="flexItem" *ngIf="canFilterByDepartment">
        <label for="department">{{ t('evaluation.department') }}</label>
        <kendo-dropdownlist
          formControlName="department"
          id="department"
          [data]="departments$ | async"
          textField="name"
          valueField="id"
          [valuePrimitive]="false"
          [defaultItem]="departmentDefaultItem"
        >
          <ng-template kendoDropDownListItemTemplate let-dataItem>
            {{ dataItem?.id ? dataItem.name : t('all') }}
          </ng-template>
          <ng-template kendoDropDownListValueTemplate let-dataItem>
            {{ dataItem?.id ? dataItem.name : t('all') }}
          </ng-template>
        </kendo-dropdownlist>
      </div>

      <div class="flexItem">
        <label for="employee">{{ t('employee') }}</label>
        <kendo-dropdownlist
          formControlName="employee"
          id="employee"
          [data]="employees$ | async"
          textField="lastName"
          valueField="id"
          [valuePrimitive]="false"
          [defaultItem]="employeeDefaultItem"
        >
          <ng-template kendoDropDownListItemTemplate let-dataItem>
            {{ dataItem?.id ? dataItem.firstName + ' ' + dataItem.lastName : t('all') }}
          </ng-template>
          <ng-template kendoDropDownListValueTemplate let-dataItem>
            {{ dataItem?.id ? dataItem.firstName + ' ' + dataItem.lastName : t('all') }}
          </ng-template>
        </kendo-dropdownlist>
      </div>

      <div class="flexItem">
        <label for="assignmentTypeIds">{{ t('evaluation.assignmentType') }}</label>
        <kendo-multiselect
          formControlName="assignmentTypeIds"
          id="assignmentTypeIds"
          [data]="assignmentTypes"
          textField="name"
          valueField="id"
          [valuePrimitive]="true"
          [placeholder]="t('all')"
          [autoClose]="false"
        ></kendo-multiselect>
      </div>

      <div class="flexItem align-bottom">
        <button
          *ngIf="filterForm.dirty"
          kendoButton
          type="button"
          fillMode="outline"
          size="medium"
          rounded="medium"
          themeColor="primary"
          (click)="onResetFilter()"
        >
          {{ t('reset') }}
        </button>
      </div>
    </div>
  </form>

  <div *ngIf="!hasFiscalYear" class="text pl">
    {{ t('evaluation.fiscalYearRequired') }}
  </div>

  <div *ngIf="isEmpty && isWorkTimeEmpty" class="text pl">
    {{ t('evaluation.noResults') }}
  </div>

  <ng-container *ngIf="hasFiscalYear && !isEmpty">
    <kendo-expansionpanel
      class="mbs"
      [title]="
        selectedScope
          ? t('evaluation.summaryTitleWithScope', { scope: selectedScope, fiscalYear: selectedFiscalYearLabel })
          : t('evaluation.summaryTitle', { fiscalYear: selectedFiscalYearLabel })
      "
      [(expanded)]="productivityExpanded"
    >
    <ng-container *ngIf="productivityExpanded">
    <div class="flex mbm" *ngIf="summary">
      <div class="flexItem box width50 mrs">
        <label class="bold pbs">{{ t('evaluation.total') }}</label>
        <div>{{ totalHours | number: '1.0-2' }} h</div>
      </div>
      <div class="flexItem box width50 mls">
        <label class="bold pbs">{{ t('evaluation.productivity') }}</label>
        <div>{{ summary.productivity | number: '1.0-2' }} %</div>
      </div>
    </div>

    <kendo-chart class="monthlyChart">
      <kendo-chart-legend position="bottom"></kendo-chart-legend>

      <kendo-chart-category-axis>
        <kendo-chart-category-axis-item></kendo-chart-category-axis-item>
      </kendo-chart-category-axis>

      <kendo-chart-value-axis>
        <kendo-chart-value-axis-item
          name="hours"
          [title]="{ text: t('evaluation.hoursAxis') }"
          [min]="0"
        ></kendo-chart-value-axis-item>
      </kendo-chart-value-axis>

      <kendo-chart-series>
        <kendo-chart-series-item
          type="column"
          axis="hours"
          [stack]="true"
          [data]="dataPoints"
          field="productiveHours"
          categoryField="label"
          [name]="t('evaluation.productive')"
          [color]="seriesColors.productive"
        ></kendo-chart-series-item>

        <kendo-chart-series-item
          type="column"
          axis="hours"
          [stack]="true"
          [data]="dataPoints"
          field="unproductiveHours"
          categoryField="label"
          [name]="t('evaluation.unproductive')"
          [color]="seriesColors.unproductive"
        ></kendo-chart-series-item>

        <kendo-chart-series-item
          type="column"
          axis="hours"
          [stack]="true"
          [data]="dataPoints"
          field="uncategorizedHours"
          categoryField="label"
          [name]="t('evaluation.uncategorized')"
          [color]="seriesColors.uncategorized"
          [visible]="hasUncategorizedTime"
          [visibleInLegend]="hasUncategorizedTime"
        ></kendo-chart-series-item>

        <!--
          Carries the productivity label above each column. Kendo refuses outsideEnd
          labels on stacked series, so this invisible line rides on the stack height
          and uses the marker-relative "above" position instead.
        -->
        <kendo-chart-series-item
          type="line"
          axis="hours"
          [data]="dataPoints"
          field="totalHours"
          categoryField="label"
          color="transparent"
          [width]="0"
          [markers]="{ visible: false }"
          [visibleInLegend]="false"
          [labels]="productivityLabels"
          [tooltip]="{ visible: false }"
        ></kendo-chart-series-item>
      </kendo-chart-series>

      <kendo-chart-tooltip>
        <ng-template kendoChartSeriesTooltipTemplate let-dataItem="dataItem" let-category="category">
          <div class="align-left">
            <strong>{{ category }}</strong>
            <div>{{ t('evaluation.total') }}: {{ dataItem.totalTime }}</div>
            <div>{{ t('evaluation.productive') }}: {{ dataItem.productiveTime }}</div>
            <div>{{ t('evaluation.unproductive') }}: {{ dataItem.unproductiveTime }}</div>
            <div *ngIf="hasUncategorizedTime">{{ t('evaluation.uncategorized') }}: {{ dataItem.uncategorizedTime }}</div>
            <div>{{ t('evaluation.productivity') }}: {{ dataItem.productivity | number: '1.0-2' }} %</div>
          </div>
        </ng-template>
      </kendo-chart-tooltip>
    </kendo-chart>
    </ng-container>
    </kendo-expansionpanel>

    <kendo-expansionpanel
      [title]="
        selectedScope
          ? t('evaluation.activitiesTitleWithScope', { scope: selectedScope, fiscalYear: selectedFiscalYearLabel })
          : t('evaluation.activitiesTitle', { fiscalYear: selectedFiscalYearLabel })
      "
      [(expanded)]="activitiesExpanded"
    >
    <ng-container *ngIf="activitiesExpanded">
    <div class="flex mbm" *ngIf="summary">
      <div class="flexItem box width50 mrs">
        <label class="bold pbs">{{ t('evaluation.total') }}</label>
        <div>{{ totalHours | number: '1.0-2' }} h</div>
      </div>
      <div class="flexItem box width50 mls">
        <label class="bold pbs">{{ t('evaluation.topUnproductive') }}</label>
        <div *ngIf="topUnproductiveCategory; else noUnproductiveTime">
          {{ topUnproductiveCategory.label }} · {{ topUnproductiveCategory.hours | number: '1.0-2' }} h
        </div>
        <ng-template #noUnproductiveTime>
          <div>–</div>
        </ng-template>
      </div>
    </div>

    <kendo-chart [style.height.px]="categoryChartHeight">
      <kendo-chart-legend [visible]="false"></kendo-chart-legend>

      <kendo-chart-category-axis>
        <kendo-chart-category-axis-item
          [majorGridLines]="{ visible: false }"
        ></kendo-chart-category-axis-item>
      </kendo-chart-category-axis>

      <kendo-chart-value-axis>
        <kendo-chart-value-axis-item
          name="categoryHours"
          [title]="{ text: t('evaluation.hoursAxis') }"
          [min]="0"
        ></kendo-chart-value-axis-item>
      </kendo-chart-value-axis>

      <kendo-chart-series>
        <kendo-chart-series-item
          type="bar"
          axis="categoryHours"
          [data]="categoryDataPoints"
          field="hours"
          categoryField="label"
          colorField="color"
          [gap]="0.6"
          [labels]="categoryLabels"
        ></kendo-chart-series-item>
      </kendo-chart-series>

      <kendo-chart-tooltip>
        <ng-template kendoChartSeriesTooltipTemplate let-dataItem="dataItem" let-category="category">
          <div class="align-left">
            <strong>{{ category }}</strong>
            <div>{{ dataItem.time }} ({{ dataItem.share | number: '1.0-2' }} %)</div>
            <div>{{ dataItem.productivityLabel }}</div>
          </div>
        </ng-template>
      </kendo-chart-tooltip>
    </kendo-chart>

    <!--
      A single series carries every activity, so its colours come from colorField and Kendo
      has nothing to put in a legend. This one is hand-built to sit where Kendo's own
      bottom legend sits in the chart above.
    -->
    <div class="chartLegend">
      <span>
        <span class="legendMarker" [style.background-color]="seriesColors.productive"></span>
        {{ t('evaluation.productive') }}
      </span>
      <span>
        <span class="legendMarker" [style.background-color]="seriesColors.unproductive"></span>
        {{ t('evaluation.unproductive') }}
      </span>
      <span *ngIf="hasUncategorizedTime">
        <span class="legendMarker" [style.background-color]="seriesColors.uncategorized"></span>
        {{ t('evaluation.uncategorized') }}
      </span>
    </div>

    <h4 class="mtl mbs">{{ t('evaluation.activitiesByMonth') }}</h4>

    <!--
      Kendo builds both heatmap axes from the order the data points arrive in, so the rows
      line up with the bar chart above. Its y axis defaults to reversed, hence [reverse].
    -->
    <kendo-chart [style.height.px]="heatmapChartHeight">
      <kendo-chart-legend [visible]="false"></kendo-chart-legend>

      <kendo-chart-x-axis>
        <kendo-chart-x-axis-item [majorGridLines]="{ visible: false }"></kendo-chart-x-axis-item>
      </kendo-chart-x-axis>

      <kendo-chart-y-axis>
        <kendo-chart-y-axis-item
          [reverse]="true"
          [majorGridLines]="{ visible: false }"
        ></kendo-chart-y-axis-item>
      </kendo-chart-y-axis>

      <kendo-chart-series>
        <kendo-chart-series-item
          type="heatmap"
          [data]="heatmapDataPoints"
          xField="month"
          yField="activity"
          field="hours"
          [color]="heatmapColor"
        ></kendo-chart-series-item>
      </kendo-chart-series>

      <kendo-chart-tooltip>
        <ng-template kendoChartSeriesTooltipTemplate let-dataItem="dataItem">
          <div class="align-left">
            <strong>{{ dataItem.activity }}</strong>
            <div>{{ dataItem.month }} · {{ dataItem.time }}</div>
          </div>
        </ng-template>
      </kendo-chart-tooltip>
    </kendo-chart>

    <div class="chartLegend">
      <span>{{ t('evaluation.few') }}</span>
      <span class="heatmapScale" [style.background]="'linear-gradient(to right, #ffffff, ' + heatmapColor + ')'"></span>
      <span>{{ t('evaluation.many') }}</span>
    </div>
    </ng-container>
    </kendo-expansionpanel>

    <kendo-expansionpanel
      *ngIf="assignmentTypeCharts.length"
      class="mts"
      [title]="
        selectedScope
          ? t('evaluation.assignmentTimeTitleWithScope', { scope: selectedScope, fiscalYear: selectedFiscalYearLabel })
          : t('evaluation.assignmentTimeTitle', { fiscalYear: selectedFiscalYearLabel })
      "
      [(expanded)]="assignmentTimeExpanded"
    >
    <ng-container *ngIf="assignmentTimeExpanded">

      <!-- One chart per AssignmentType; the heading carries the type's total over all its assignments. -->
      <div *ngFor="let chart of assignmentTypeCharts; let isFirst = first" [class.mtl]="!isFirst">
        <h4 class="mbs">{{ chart.label }} · {{ chart.totalHours | number: '1.0-2' }} h</h4>

        <kendo-chart
          [style.height.px]="chart.height"
          (seriesClick)="onAssignmentSeriesClick($event)"
          (axisLabelClick)="onAssignmentAxisLabelClick($event, chart)"
        >
          <kendo-chart-legend position="bottom"></kendo-chart-legend>

          <kendo-chart-category-axis>
            <kendo-chart-category-axis-item
              [majorGridLines]="{ visible: false }"
              [labels]="assignmentAxisLabels"
            ></kendo-chart-category-axis-item>
          </kendo-chart-category-axis>

          <kendo-chart-value-axis>
            <kendo-chart-value-axis-item
              name="assignmentHours"
              [title]="{ text: t('evaluation.hoursAxis') }"
              [min]="0"
            ></kendo-chart-value-axis-item>
          </kendo-chart-value-axis>

          <!-- One stacked series per employee, so each assignment's bar splits by person. -->
          <kendo-chart-series>
            <kendo-chart-series-item
              *ngFor="let series of chart.series"
              type="bar"
              axis="assignmentHours"
              [stack]="true"
              [data]="series.data"
              field="hours"
              categoryField="assignment"
              [name]="series.name"
            ></kendo-chart-series-item>
          </kendo-chart-series>

          <kendo-chart-tooltip>
            <ng-template kendoChartSeriesTooltipTemplate let-dataItem="dataItem" let-category="category">
              <div class="align-left">
                <strong>{{ category }}</strong>
                <div>{{ dataItem.employee }}: {{ dataItem.time }}</div>
                <div>{{ t('evaluation.total') }}: {{ dataItem.assignmentTotal }}</div>
              </div>
            </ng-template>
          </kendo-chart-tooltip>
        </kendo-chart>
      </div>
    </ng-container>
    </kendo-expansionpanel>
  </ng-container>

  <kendo-expansionpanel
    *ngIf="hasFiscalYear && !isWorkTimeEmpty"
    class="mts"
    [title]="
      selectedScope
        ? t('evaluation.workTimeTitleWithScope', { scope: selectedScope, fiscalYear: selectedFiscalYearLabel })
        : t('evaluation.workTimeTitle', { fiscalYear: selectedFiscalYearLabel })
    "
    [(expanded)]="workTimeExpanded"
  >
  <ng-container *ngIf="workTimeExpanded">
    <div class="flex mbm" *ngIf="workTimeSummary">
      <div class="flexItem box width50 mrs">
        <label class="bold pbs">{{ t('evaluation.targetTime') }}</label>
        <div>{{ workTimeTotalHours | number: '1.0-2' }} h</div>
      </div>
      <div class="flexItem box width50 mls">
        <label class="bold pbs">{{ t('evaluation.difference') }}</label>
        <div [class.deficit]="hasWorkTimeDeficit">{{ workTimeDifference }}</div>
      </div>
    </div>

    <div class="text mbm" *ngIf="workTimeEndDate">
      {{ t('evaluation.workTimeHint', { date: workTimeEndDate | date: 'dd.MM.yyyy' }) }}
    </div>

    <!-- Target time carries no assignment, so this chart deliberately ignores that filter. -->
    <div class="text mbm" *ngIf="hasAssignmentTypeFilter">
      {{ t('evaluation.workTimeAssignmentTypeHint') }}
    </div>

    <kendo-chart [style.height.px]="workTimeChartHeight">
      <kendo-chart-legend position="bottom"></kendo-chart-legend>

      <kendo-chart-category-axis>
        <kendo-chart-category-axis-item
          [majorGridLines]="{ visible: false }"
        ></kendo-chart-category-axis-item>
      </kendo-chart-category-axis>

      <kendo-chart-value-axis>
        <kendo-chart-value-axis-item
          name="workTimeHours"
          [title]="{ text: t('evaluation.hoursAxis') }"
          [min]="0"
        ></kendo-chart-value-axis-item>
      </kendo-chart-value-axis>

      <!-- Two unstacked bar series render as a pair per employee, Soll above Ist. -->
      <kendo-chart-series>
        <kendo-chart-series-item
          type="bar"
          axis="workTimeHours"
          [data]="workTimeDataPoints"
          field="targetHours"
          categoryField="employee"
          [name]="t('evaluation.targetTime')"
          [color]="workTimeColors.target"
        ></kendo-chart-series-item>

        <kendo-chart-series-item
          type="bar"
          axis="workTimeHours"
          [data]="workTimeDataPoints"
          field="actualHours"
          categoryField="employee"
          [name]="t('evaluation.actualTime')"
          [color]="workTimeColors.actual"
        ></kendo-chart-series-item>
      </kendo-chart-series>

      <kendo-chart-tooltip>
        <ng-template kendoChartSeriesTooltipTemplate let-dataItem="dataItem" let-category="category">
          <div class="align-left">
            <strong>{{ category }}</strong>
            <div>{{ t('evaluation.targetTime') }}: {{ dataItem.targetTime }}</div>
            <div>{{ t('evaluation.actualTime') }}: {{ dataItem.actualTime }}</div>
            <div>{{ t('evaluation.difference') }}: {{ dataItem.differenceTime }}</div>
          </div>
        </ng-template>
      </kendo-chart-tooltip>
    </kendo-chart>
  </ng-container>
  </kendo-expansionpanel>
</ng-container>
